import { Tabs, TabsList, TabsTrigger } from '@artstorefronts/ui';
import { Users, Image, Zap, BarChart2, UserSquare2, Images } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AdminLayout from '../../components/AdminLayout';
import StatsCardChart from '../../components/StatsCardChart';

const GET_REPORTS = gql`
  query GetReports($start: Float!, $end: Float!, $term: Term!) {
    reports(start: $start, end: $end, term: $term) {
      totalUsers {
        count
        series {
          period
          value
        }
      }
      totalCompletions {
        count
        series {
          period
          value
        }
      }
      totalImages {
        count
        series {
          period
          value
        }
      }
      averageCompletionsPerUser {
        count
        series {
          period
          value
        }
      }
      averageImagesPerUser {
        count
        series {
          period
          value
        }
      }
      averageCompletionsPerImage {
        count
        series {
          period
          value
        }
      }
    }
  }
`;

interface DateRange {
  label: string;
  days: number;
  term: 'day' | 'week' | 'month';
}

function MetricsCards({ selectedRange }: { selectedRange: DateRange }) {
  const { t } = useTranslation('admin');
  const end = Math.floor(Date.now() / 1000);
  const start = end - selectedRange.days * 24 * 60 * 60;

  const { data, loading } = useQuery(GET_REPORTS, {
    variables: {
      start,
      end,
      term: selectedRange.term,
    },
  });

  if (loading && !data) return null;

  const metrics = [
    {
      title: t('metrics.cards.total-users.title'),
      metric: data?.reports.totalUsers,
      description: t('metrics.cards.total-users.description'),
      icon: Users,
    },
    {
      title: t('metrics.cards.total-completions.title'),
      metric: data?.reports.totalCompletions,
      description: t('metrics.cards.total-completions.description'),
      icon: Zap,
    },
    {
      title: t('metrics.cards.total-images.title'),
      metric: data?.reports.totalImages,
      description: t('metrics.cards.total-images.description'),
      icon: Image,
    },
    {
      title: t('metrics.cards.images-per-user.title'),
      metric: data?.reports.averageImagesPerUser,
      description: t('metrics.cards.images-per-user.description'),
      icon: UserSquare2,
    },
    {
      title: t('metrics.cards.completions-per-user.title'),
      metric: data?.reports.averageCompletionsPerUser,
      description: t('metrics.cards.completions-per-user.description'),
      icon: BarChart2,
    },
    {
      title: t('metrics.cards.completions-per-image.title'),
      metric: data?.reports.averageCompletionsPerImage,
      description: t('metrics.cards.completions-per-image.description'),
      icon: Images,
    },
  ];

  return (
    <div className="grid gap-6 md:grid-cols-3">
      {metrics.map((metric) => (
        <StatsCardChart key={metric.title} {...metric} />
      ))}
    </div>
  );
}

function AdminMetrics() {
  const { t } = useTranslation('admin');

  const DATE_RANGES: DateRange[] = [
    { label: t('metrics.range.7d'), days: 7, term: 'day' },
    { label: t('metrics.range.30d'), days: 30, term: 'day' },
    { label: t('metrics.range.3m'), days: 90, term: 'day' },
    { label: t('metrics.range.6m'), days: 180, term: 'day' },
    { label: t('metrics.range.1y'), days: 365, term: 'day' },
    { label: t('metrics.range.all'), days: 730, term: 'day' },
  ];

  const [selectedRange, setSelectedRange] = useState<DateRange>(() => ({
    label: t('metrics.range.30d'),
    days: 30,
    term: 'day',
  }));

  return (
    <AdminLayout title={t('metrics.page-title')} description={t('metrics.page-description')}>
      <div className="flex justify-end mb-6">
        <Tabs defaultValue={selectedRange.days.toString()}>
          <TabsList>
            {DATE_RANGES.map((range) => (
              <TabsTrigger key={range.days} value={range.days.toString()} onClick={() => setSelectedRange(range)}>
                {range.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      <MetricsCards selectedRange={selectedRange} />
    </AdminLayout>
  );
}

export default AdminMetrics;
